import { PackageSearch, Trash2 } from 'lucide-react';
import { IconButton, Input, NumberStepper, formatMoney } from '@platform/design-system';
import { Controller, type FieldArrayWithId } from 'react-hook-form';
import { usePageContext } from '../usePageContext.ts';
import {
  lineTotalOf,
  ORDER_FORM_LIMITS,
  type NewOrderFormValues,
  type OrderLineValues,
} from './new-order-form.ts';
import type { OrderFormState } from './OrderForm.tsx';
import styles from './NewOrder.module.css';

export type OrderLinesProps = {
  form: OrderFormState;
  fields: readonly FieldArrayWithId<NewOrderFormValues, 'lines'>[];
  watchedLines: readonly OrderLineValues[];
  currency: string;
  onRemove: (index: number) => void;
};

const EmptyLines = ({ invalid }: { invalid: boolean }) => {
  const { t } = usePageContext();
  return (
    <div className={[styles['empty'], invalid ? styles['emptyInvalid'] : ''].join(' ')}>
      <span className={styles['emptyIcon']} aria-hidden="true">
        <PackageSearch size={20} />
      </span>
      <p className={styles['emptyTitle']} role={invalid ? 'alert' : undefined}>
        {t('newOrder.noLines')}
      </p>
      <p className={styles['muted']}>{t('newOrder.emptyLinesHint')}</p>
    </div>
  );
};

export const OrderLines = ({ form, fields, watchedLines, currency, onRemove }: OrderLinesProps) => {
  const { t, locale } = usePageContext();
  const { errors } = form.formState;
  if (fields.length === 0) return <EmptyLines invalid={errors.lines !== undefined} />;
  return (
    <ul className={styles['lines']}>
      {fields.map((field, index) => (
        <li key={field.id} className={styles['line']}>
          <span className={styles['lineName']}>
            <span className={styles['lineTitle']}>{field.label}</span>
            <span className={styles['sku']}>{field.sku}</span>
          </span>
          <Controller
            control={form.control}
            name={`lines.${String(index)}.quantity` as `lines.${number}.quantity`}
            render={({ field: quantity }) => (
              <NumberStepper
                label={t('orders.quantity')}
                min={1}
                max={ORDER_FORM_LIMITS.quantity}
                decrementLabel={t('newOrder.decreaseQuantity')}
                incrementLabel={t('newOrder.increaseQuantity')}
                errorText={errors.lines?.[index]?.quantity ? t('newOrder.quantityInvalid') : undefined}
                value={quantity.value}
                name={quantity.name}
                ref={quantity.ref}
                onBlur={quantity.onBlur}
                onValueChange={quantity.onChange}
              />
            )}
          />
          <Input
            label={t('orders.unitPrice')}
            inputMode="decimal"
            suffix={currency}
            errorText={errors.lines?.[index]?.unitPrice ? t('newOrder.amountInvalid') : undefined}
            {...form.register(`lines.${String(index)}.unitPrice` as `lines.${number}.unitPrice`)}
          />
          <span className={styles['lineTotal']}>
            {formatMoney({ amount: lineTotalOf(watchedLines[index] ?? field), currency }, locale)}
          </span>
          <IconButton
            label={t('newOrder.remove')}
            icon={<Trash2 size={16} aria-hidden="true" />}
            size="sm"
            className={styles['remove']}
            onClick={() => {
              onRemove(index);
            }}
          />
        </li>
      ))}
    </ul>
  );
};
